<template>
  <div class="min-h-screen flex items-center justify-center px-4 bg-lightGray">
    <div class="max-w-md w-full bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-xl text-center space-y-3">
      <h1 class="text-xl font-semibold text-gray-900">{{ statusMessage }}</h1>
      <p v-if="errorMessage" class="text-sm text-red-600 leading-relaxed">{{ errorMessage }}</p>
      <p v-else class="text-sm text-gray-600">Please wait while we sign you in.</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { decodeJWT } from '@/utils/jwtDecoder'
import { verifyMagicLinkToken } from '@/services/magicLinkService'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const statusMessage = ref('Signing you in...')
const errorMessage = ref<string | null>(null)

onMounted(async () => {
  const token = typeof route.query.token === 'string' ? route.query.token : ''

  if (!token) {
    statusMessage.value = 'Sign-in failed'
    errorMessage.value = 'This sign-in link is missing a token. Please request a new one.'
    setTimeout(() => router.push('/login'), 2500)
    return
  }

  try {
    const data = await verifyMagicLinkToken(token)

    if (data.jwt_access) localStorage.setItem('token', String(data.jwt_access).trim())
    if (data.jwt_refresh) localStorage.setItem('refresh_token', String(data.jwt_refresh).trim())

    const decoded = decodeJWT(data.jwt_access)
    if (decoded?.id) {
      localStorage.setItem('user_id', String(decoded.id))
    }

    await authStore.initAuth()
    statusMessage.value = 'Sign in successful!'

    if (!data.is_profile_complete) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      router.push('/complete-profile')
      return
    }

    const redirectTo = localStorage.getItem('redirectAfterLogin')
    if (redirectTo) {
      localStorage.removeItem('redirectAfterLogin')
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      router.push(redirectTo)
      return
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.push('/')
  } catch (err: unknown) {
    const ax = err as { response?: { data?: { error?: string; detail?: string } } }
    const message =
      ax.response?.data?.error ||
      ax.response?.data?.detail ||
      'This sign-in link is invalid or expired. Please request a new one.'

    statusMessage.value = 'Sign-in failed'
    errorMessage.value = String(message)
    setTimeout(() => router.push('/login'), 3000)
  }
})
</script>
